import { Show, createMemo } from "solid-js";
import type { Accessor } from "solid-js";
import styles from "../../pages/Inventory.module.css";
import ItemSlotGrid, { type ItemSlotGridEntry } from "./ItemSlotGrid";
import { fromItemDef } from "../../services/itemRefAdapters";
import { itemStackSignature } from "../../models/itemStack";
import Text from "../Text";
import StorageSelect from "../StorageSelect";
import { useTranslation } from "../../hooks/useTranslation";
import type { ItemDef } from "../../services/craftingResolver";
import type { StorageInfo } from "../../services/inventoryApi";
import type { DragSelection, SlotData, SortMode } from "./inventoryUtils";
import { formatQuantityCompact, quantityBreakdown } from "../../services/quantityFormatter";
import { formatInteger } from "../../services/numberFormat";

export interface InventoryPanelProps {
  panel: "left" | "right";
  storageId: Accessor<string | null>;
  slots: Accessor<SlotData[]>;
  search: Accessor<string>;
  setSearch: (v: string) => void;
  sort: Accessor<SortMode>;
  setSort: (v: SortMode) => void;
  storages: Accessor<StorageInfo[]>;
  selectPanelStorage: (panel: "left" | "right", id: string) => void;
  dragData: Accessor<DragSelection | null>;
  dropTarget: Accessor<"left" | "right" | null>;
  handleDragStart: (e: DragEvent, itemKey: string, panel: "left" | "right") => void;
  handleDragOver: (e: DragEvent, panel: "left" | "right") => void;
  handleDragLeave: (e: DragEvent, panel: "left" | "right") => void;
  handleDrop: (e: DragEvent, panel: "left" | "right") => void;
  handleDragEnd: () => void;
  selected: Accessor<ItemDef | null>;
  selectedKeys: Accessor<Set<string>>;
  selectedPanel: Accessor<"left" | "right">;
  onSelectItem: (panel: "left" | "right", itemKey: string, event: MouseEvent | KeyboardEvent) => void;
  changedKeys: Accessor<Map<string, "up" | "down">>;
  onLagerscan: () => void;
  lagerscanLoading: Accessor<boolean>;
}

export default function InventoryPanel(props: InventoryPanelProps) {
  const { t } = useTranslation();
  const panel = props.panel;
  const isDropTarget = () => props.dropTarget() === panel;
  const isDragging = () => {
    const d = props.dragData();
    return d !== null && d.sourcePanel !== panel;
  };

  // The badge shows the one number the user acts on, so the hover carries the rest of the ledger:
  // the DK breakdown first, then the plain counts behind it.
  const quantityRows = (slot: SlotData) => {
    const badgeQty = slot.reserved > 0 ? slot.available : slot.qty;
    const breakdown = quantityBreakdown(badgeQty, slot.item.stackSize);
    return (
      <div class={styles.tooltipRows}>
        <Show when={breakdown}>
          <div class={styles.tooltipBreakdown}>{breakdown}</div>
        </Show>
        <div class={styles.detailRow}>
          <span class={styles.detailLabel}><Text val="INVENTORY_AVAILABLE" /></span>
          <span class={styles.detailValue}>{formatInteger(slot.available)}</span>
        </div>
        <Show when={slot.reserved > 0}>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_RESERVED" /></span>
            <span class={`${styles.detailValue} ${styles.reservedValue}`}>{formatInteger(slot.reserved)}</span>
          </div>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_TOTAL" /></span>
            <span class={styles.detailValue}>{formatInteger(slot.qty)}</span>
          </div>
        </Show>
        <Show when={slot.totalQty > slot.qty}>
          <div class={styles.detailRow}>
            <span class={styles.detailLabel}><Text val="INVENTORY_STOCK_ALL" /></span>
            <span class={`${styles.detailValue} ${styles.otherStorage}`}>{formatInteger(slot.totalQty)}</span>
          </div>
        </Show>
      </div>
    );
  };

  return (
    <div
      class={`${styles.panel} ${isDropTarget() ? styles.panelDropTarget : ""}`}
      onDragOver={(e) => props.handleDragOver(e, panel)}
      onDragLeave={(e) => props.handleDragLeave(e, panel)}
      onDrop={(e) => props.handleDrop(e, panel)}
    >
      <div class={styles.panelToolbar}>
        <StorageSelect
          storages={props.storages()}
          value={props.storageId() ?? ""}
          onChange={(storageId) => props.selectPanelStorage(panel, storageId)}
        />
        <button
          type="button"
          class={styles.storageBtn}
          title={t("INVENTORY_LAGERSCAN")}
          disabled={!props.storageId() || props.lagerscanLoading()}
          onClick={props.onLagerscan}
        >
          <Show when={props.lagerscanLoading()} fallback={<i class="fa-solid fa-satellite-dish" />}>
            <i class="fa-solid fa-spinner fa-spin" />
          </Show>
        </button>
      </div>

      <div class={styles.panelControls}>
        <div class={styles.searchWrap}>
          <input
            class={styles.searchBar}
            type="text"
            placeholder={t("INVENTORY_SEARCH_PLACEHOLDER")}
            value={props.search()}
            onInput={(e) => props.setSearch(e.currentTarget.value)}
          />
          <Show when={props.search()}>
            <span class={styles.searchClear} onClick={() => props.setSearch("")}>&times;</span>
          </Show>
        </div>
        <select
          class={styles.sortSelect}
          value={props.sort()}
          onChange={(e) => props.setSort(e.currentTarget.value as SortMode)}
        >
          <option value="name">{t("INVENTORY_SORT_AZ")}</option>
          <option value="qty-desc">{t("INVENTORY_SORT_QTY_DESC")}</option>
          <option value="qty-asc">{t("INVENTORY_SORT_QTY_ASC")}</option>
          <option value="id">ID</option>
        </select>
      </div>

      <Show when={props.selectedPanel() === panel && props.selectedKeys().size > 1}>
        <div class={styles.selectionBar}>
          <span class={styles.selectionCount}>{t("INVENTORY_SELECTED_COUNT", props.selectedKeys().size)}</span>
          <span class={styles.selectionHint}><Text val="INVENTORY_SELECT_HINT" /></span>
        </div>
      </Show>

      <div class={styles.gridArea}>
        <Show when={isDragging()}>
          <div class={`${styles.dropOverlay} ${isDropTarget() ? styles.dropOverlayActive : ""}`}>
            <div class={styles.dropOverlayContent}>
              <i class="fa-solid fa-arrow-down" />
              <span><Text val="INVENTORY_DROP_HERE" /></span>
            </div>
          </div>
        </Show>

        {(() => {
          // <For> diffs tiles by object identity, so a rebuilt entry throws away that tile's icon,
          // tooltip and badge DOM even when the item is unchanged.
          const tilesByKey = new Map<string, { signature: string; entry: ItemSlotGridEntry }>();

          const entries = createMemo<ItemSlotGridEntry[]>(() => {
            const storageId = props.storageId();
            const changed = props.changedKeys();
            const selectedKeys = props.selectedKeys();
            const panelHoldsSelection = props.selectedPanel() === panel;
            const stillListed = new Set<string>();

            const tiles = props.slots().map((slot) => {
              stillListed.add(slot.item.key);
              const changeDirection = changed.get(`${storageId}:${slot.item.key}`);
              // A zero count is an empty slot to ItemStackView; the badge carries the amount anyway.
              const stack = slot.stack
                ? { ...slot.stack, count: Math.max(slot.qty, 1) }
                : fromItemDef(slot.item);
              const selected = panelHoldsSelection && selectedKeys.has(slot.item.key);
              const signature = [
                slot.qty, slot.totalQty, slot.reserved, slot.available, slot.status, slot.hasRecipe,
                changeDirection ?? "", selected, itemStackSignature(stack),
              ].join("|");
              const rendered = tilesByKey.get(slot.item.key);
              if (rendered?.signature === signature) return rendered.entry;

              const extraClass = [
                slot.reserved > 0 ? styles.slotReserved : "",
                changeDirection === "up" ? styles.slotGained : changeDirection === "down" ? styles.slotLost : "",
              ].filter(Boolean).join(" ");

              const badge = slot.qty > 0
                ? <span class={`${styles.badge} ${slot.reserved > 0 ? styles.badgeReserved : ""}`}>
                  {formatQuantityCompact(slot.reserved > 0 ? slot.available : slot.qty, slot.item.stackSize)}
                </span>
                : slot.hasRecipe && slot.status === "Craftable"
                  ? <span class={`${styles.badge} ${styles.craftable}`}><Text val="INVENTORY_CRAFT" /></span>
                  : slot.totalQty > 0
                    ? <span class={`${styles.badge} ${styles.otherStorage}`}><i class="fa-solid fa-warehouse" /></span>
                    : slot.hasRecipe
                      ? <span class={`${styles.badge} ${styles.craftDisabled}`}><Text val="INVENTORY_CRAFT" /></span>
                      : undefined;

              const entry: ItemSlotGridEntry = {
                key: slot.item.key,
                label: slot.item.displayName,
                stack,
                badge,
                extraClass,
                selected,
                draggable: slot.available > 0,
                tooltipExtra: slot.qty > 0 || slot.totalQty > 0 ? quantityRows(slot) : undefined,
              };
              tilesByKey.set(slot.item.key, { signature, entry });
              return entry;
            });

            for (const key of tilesByKey.keys()) {
              if (!stillListed.has(key)) tilesByKey.delete(key);
            }
            return tiles;
          });

          return (
            <ItemSlotGrid
              items={entries()}
              emptyMessage={props.search() ? <Text val="INVENTORY_NO_MATCH" /> : <Text val="INVENTORY_EMPTY" />}
              onSelect={(key, event) => props.onSelectItem(panel, key, event)}
              onDragStart={(e, key) => props.handleDragStart(e, key, panel)}
              onDragEnd={props.handleDragEnd}
            />
          );
        })()}
      </div>
    </div>
  );
}
